import React, { useState, useEffect } from "react";
import { Warehouse } from "../../types/Warehouse";
import { useFetchContext } from "../../context/FetchContext";
import { useToast } from "../../context/ToastContext";
import { IDataPagination } from "types/User";
import Select from "components/ui/Select";
import Table from "components/ui/Table";
import Pagination from "components/ui/Pagination";

interface SerialNumber {
  productCode: string;
  serialNumber: string;
  warehouseId: number;
  warehouseLocation: string;
  quantity: number;
}

interface SerialNumbersResponse {
  total: number;
  totalPages: number;
  items: SerialNumber[];
}

const SerialNumbersList: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [serialNumbers, setSerialNumbers] = useState<SerialNumber[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("productCode");
  const [orderType, setOrderType] = useState<string>("ASC");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const {
    fetchData,
    state: { isLoading },
  } = useFetchContext();
  const { showToast } = useToast();

  useEffect(() => {
    const getWarehouses = async () => {
      try {
        const response = await fetchData<IDataPagination<Warehouse>>(
          "/warehouses"
        );
        if (response && response.data && response.data.items) {
          setWarehouses(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        showToast("Errore durante il caricamento dei magazzini", "error");
      }
    };

    getWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchSerialNumbers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse, currentPage, orderBy, orderType]);

  const fetchSerialNumbers = async () => {
    try {
      const queryParams = new URLSearchParams({
        warehouseId: selectedWarehouse,
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        searchKeyword: searchKeyword,
        orderType: orderType,
        orderBy: orderBy,
      });

      const response = await fetchData<SerialNumbersResponse>(
        `/products/locations?${queryParams.toString()}`
      );

      if (response && response.data) {
        setSerialNumbers(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.total);
      }
    } catch (error) {
      console.error("Error fetching serial numbers:", error);
      showToast("Errore durante il caricamento delle matricole", "error");
    }
  };

  const handleWarehouseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedWarehouse(e.target.value);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchKeyword(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchSerialNumbers();
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleSort = (column: string) => {
    if (orderBy === column) {
      setOrderType(orderType === "ASC" ? "DESC" : "ASC");
    } else {
      setOrderBy(column);
      setOrderType("ASC");
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h2 className="text-lg font-medium text-gray-900">Lista Matricole</h2>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="warehouse"
              className="block text-sm font-medium text-gray-700"
            >
              Seleziona Magazzino
            </label>
            <Select
              id="warehouse"
              value={selectedWarehouse}
              onChange={handleWarehouseChange}
              className="mt-1"
              fullWidth
            >
              <option value="">Seleziona un magazzino</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700"
            >
              Ricerca
            </label>
            <input
              type="text"
              id="search"
              value={searchKeyword}
              onChange={handleSearch}
              placeholder="Cerca per codice prodotto o matricola"
              className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="text-center py-4">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-gray-500">Caricamento dati...</p>
          </div>
        ) : !selectedWarehouse ? (
          <div className="text-center py-4">
            <p className="text-gray-500">
              Seleziona un magazzino per visualizzare i dati
            </p>
          </div>
        ) : (
          <>
            <Table
              data={serialNumbers}
              columns={[
                {
                  key: "productCode",
                  header: "Codice Prodotto",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm font-medium text-gray-900">
                      {item.productCode}
                    </div>
                  ),
                },
                {
                  key: "serialNumber",
                  header: "Matricola",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-500">
                      {item.serialNumber}
                    </div>
                  ),
                },
                {
                  key: "warehouseLocation",
                  header: "Posizione",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-500">
                      {item.warehouseLocation}
                    </div>
                  ),
                },
              ]}
              keyExtractor={(item) =>
                `${item.productCode}-${item.serialNumber}`
              }
              emptyMessage="Nessuna matricola trovata"
              sortColumn={orderBy}
              sortDirection={orderType as "ASC" | "DESC"}
              onSort={handleSort}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              summaryText={`Showing ${
                (currentPage - 1) * pageSize + 1
              } to ${Math.min(
                currentPage * pageSize,
                totalItems
              )} of ${totalItems} matricole`}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SerialNumbersList;
